import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import { useProducts } from "@/hooks/use-products";
import { useCreateSale, useUpdateSale } from "@/hooks/use-sales";
import { getErrorMessage } from "@/lib/error-message";
import { newIdempotencyKey } from "@/lib/idempotency";
import { queryKeys } from "@/lib/query-keys";
import { stockService } from "@/services/stock";

import {
  defaultSaleValues,
  emptySaleLine,
  saleSchema,
  type SaleFormValues,
} from "./sale-schema";
import { calculateGrandTotal, saleToFormValues } from "./sale-utils";

import type { Sale } from "@artisancode/api-types";

export function useSaleForm(
  open: boolean,
  editing: Sale | null,
  onSuccess: () => void,
) {
  const navigate = useNavigate();
  const { data: productsData } = useProducts({ per_page: 100 });
  const products = productsData?.items ?? [];
  const { mutate: create, isPending: isCreating } = useCreateSale();
  const { mutate: update, isPending: isUpdating } = useUpdateSale();
  const { data: me } = useMe();
  // One key per opened form: a retry after a network error reuses it, so the
  // server returns the sale it already booked instead of selling twice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey("sale"), [open]);

  const { data: stocksData } = useQuery({
    queryKey: queryKeys.stocks.list({ type: "product" }),
    queryFn: () =>
      stockService.list({
        type: "product",
        page: 1,
        per_page: 200,
      }),
    enabled: open,
  });

  const stockByProductId = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of stocksData?.items ?? []) {
      map.set(item.itemId, item.onHandQty);
    }
    return map;
  }, [stocksData]);

  const form = useForm<SaleFormValues>({
    resolver: zodResolver(saleSchema),
    defaultValues: defaultSaleValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lines",
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editing
          ? saleToFormValues(editing)
          : { ...defaultSaleValues, workspaceId: me?.workspaceId ?? "" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  const watchedLines = useWatch({ control: form.control, name: "lines" });
  const grandTotal = calculateGrandTotal(watchedLines);

  function selectProduct(index: number, productId: string) {
    form.setValue(`lines.${index}.productId`, productId);
    const product = products.find((p) => p.id === productId);
    if (product?.sellingPrice != null) {
      form.setValue(`lines.${index}.sellingPrice`, product.sellingPrice);
    }
  }

  function addLine() {
    append(emptySaleLine);
  }

  function onSubmit(values: SaleFormValues) {
    const items = values.lines.map((l) => ({
      productId: l.productId,
      qty: l.qty,
      sellingPrice: l.sellingPrice,
    }));

    if (editing) {
      update(
        {
          id: editing.id,
          date: values.date,
          channel: values.channel,
          customerName: values.customerName,
          transactionCode: values.transactionCode,
          paymentStatus: values.paymentStatus,
          paymentMethod: values.paymentMethod,
          notes: values.notes,
          // Items are locked once Piutang has payments for this sale.
          items: editing.hasReceivablePayments ? undefined : items,
        },
        {
          onSuccess: () => {
            toast.success("Penjualan berhasil diperbarui.");
            onSuccess();
          },
          onError: (err: Error) => {
            const message = getErrorMessage(
              err,
              "Gagal memperbarui penjualan.",
            );
            if (
              message.toLowerCase().includes("produksi") ||
              message.toLowerCase().includes("stok")
            ) {
              toast.error(message, {
                action: {
                  label: "Buat Produksi",
                  onClick: () => navigate("/production"),
                },
              });
            } else {
              toast.error(message);
            }
          },
        },
      );
    } else {
      // Left empty → the server assigns the next sequential number
      const resolvedTransactionCode =
        values.transactionCode?.trim() || undefined;

      create(
        {
          body: {
            date: values.date,
            channel: values.channel,
            customerName: values.customerName,
            transactionCode: resolvedTransactionCode,
            paymentStatus: values.paymentStatus,
            paymentMethod: values.paymentMethod,
            notes: values.notes,
            items,
            workspaceId: values.workspaceId || undefined,
          },
          idempotencyKey,
        },
        {
          onSuccess: () => {
            toast.success("Penjualan berhasil ditambahkan.");
            onSuccess();
          },
          onError: (err: Error) => {
            const message = getErrorMessage(
              err,
              "Gagal menambahkan penjualan.",
            );
            if (
              message.toLowerCase().includes("produksi") ||
              message.toLowerCase().includes("stok")
            ) {
              toast.error(message, {
                action: {
                  label: "Buat Produksi",
                  onClick: () => navigate("/production"),
                },
              });
            } else {
              toast.error(message);
            }
          },
        },
      );
    }
  }

  return {
    form,
    fields,
    products,
    stockByProductId,
    grandTotal,
    selectProduct,
    addLine,
    removeLine: remove,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: isCreating || isUpdating,
  };
}
